import { Divider } from '@neo4j-ndl/react';
import { DbmsIcon, ServerIconOutline } from '@neo4j-ndl/react/icons';
import { type FleetManagementTypes, consoleApi, useActiveProject } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';

import { ExploreIconOutline } from '../../../../upx/src/components/icons';
import { timePeriodFormatter } from '../../shared/ui-helpers';
import { useTokens } from '../hooks/use-tokens';
import { DeploymentStatus } from '../shared/components/deployment-status';
import { PropertyDisplay } from '../shared/components/property-display';
import { UnmonitoredServersBadge, hasUnmonitoredServers } from '../shared/components/server-status';
import { TokenStatus } from '../token-management';
import type { TypedEntity } from './topology-graph';

const DbmsCard = ({ dbms }: { dbms: FleetManagementTypes.DbmsDetails }) => {
  const { tokens } = useTokens();
  const activeProject = useActiveProject();
  const getProjectMembersRes = consoleApi.useListProjectMembersQuery(activeProject.id);
  const token = tokens.find((t) => t.claimed_by === dbms.dbmsId);
  const creator = getProjectMembersRes.data?.find((member) => member.userId === token?.created_by)?.email;

  return (
    <>
      <div className="flex w-full min-w-32 flex-col flex-wrap gap-x-4 gap-y-1">
        <div className="flex w-full gap-1 overflow-hidden whitespace-nowrap">
          <DbmsIcon className="n-size-token-7 flex-shrink-0" />
          <p className="overflow-hidden overflow-ellipsis whitespace-nowrap">{dbms.name}</p>
        </div>
        <DeploymentStatus status={dbms.status} />
        <TokenStatus token={token} />
        {hasUnmonitoredServers(dbms) && <UnmonitoredServersBadge viewType="graph" />}
        <Divider orientation="horizontal" />
        <div className="w-full">
          <PropertyDisplay label="Version" text={dbms.version} />
          <PropertyDisplay label="Edition" text={dbms.edition} />
          <PropertyDisplay label="Packaging" text={dbms.packaging} />
          <PropertyDisplay label="Registered by" text={creator ?? 'unknown user'} />
        </div>
      </div>
    </>
  );
};

const ServerCard = ({ server }: { server: FleetManagementTypes.Server }) => {
  return (
    <>
      <div className="flex min-w-32 flex-col flex-wrap gap-x-4 gap-y-1">
        <div className="flex w-full gap-1 overflow-hidden whitespace-nowrap">
          <ServerIconOutline className="n-size-token-7 flex-shrink-0" />
          <p className="overflow-hidden overflow-ellipsis whitespace-nowrap">{server.address}</p>
        </div>
        <DeploymentStatus status={server.status} />
        <Divider orientation="horizontal" />
        <div className="w-full">
          <PropertyDisplay label="Health" text={server.health} />
          <PropertyDisplay label="Mode constraint" text={server.modeConstraint} />
          <PropertyDisplay label="Version" text={server.version} />
          <PropertyDisplay label="Last ping" text={timePeriodFormatter(new Date(server.lastPing))} />
        </div>
      </div>
    </>
  );
};

const DatabaseCard = ({ database }: { database: FleetManagementTypes.Database }) => {
  return (
    <>
      <div className="flex min-w-32 flex-col flex-wrap gap-x-4 gap-y-1">
        <div className="flex w-full gap-1 overflow-hidden whitespace-nowrap">
          <ExploreIconOutline className="n-size-token-7 flex-shrink-0" />
          <p className="overflow-hidden overflow-ellipsis whitespace-nowrap">{database.name}</p>
        </div>
      </div>
    </>
  );
};

export const EntityCard = ({ entity }: { entity: TypedEntity }) => {
  return (
    <div className="border-neutral-border-weak shadow-overlay bg-palette-neutral-bg-weak relative max-w-[30vw] shrink-0 grow basis-[136px] rounded-2xl border">
      <div className="mb-4 flex w-full items-start justify-between gap-4 px-4 pt-4">
        {isNotNullish(entity.dbms) && <DbmsCard dbms={entity.dbms} />}
        {isNotNullish(entity.server) && <ServerCard server={entity.server} />}
        {isNotNullish(entity.database) && <DatabaseCard database={entity.database} />}
      </div>
    </div>
  );
};
